import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Globe,
  Hash,
  Settings,
  Target,
  FileText,
  X
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function SEOContent({ onBack, mode = 'create' }) {
  const { seoId } = useParams();
  
  // Initialize form data function
  const initializeFormData = (data = null) => {
    return {
      page_key: data?.page_key || '',
      page_name: data?.page_name || '',
      title: data?.title || '',
      description: data?.description || '',
      keywords: data?.keywords || '',
      og_title: data?.og_title || '',
      og_description: data?.og_description || '',
      og_image: data?.og_image || '',
      og_type: data?.og_type || 'website',
      twitter_title: data?.twitter_title || '',
      twitter_description: data?.twitter_description || '',
      twitter_image: data?.twitter_image || '',
      twitter_card_type: data?.twitter_card_type || 'summary_large_image',
      canonical_url: data?.canonical_url || '',
      robots_meta: data?.robots_meta || 'index, follow',
      structured_data: data?.structured_data ? JSON.stringify(data.structured_data, null, 2) : '{}',
      h1_tag: data?.h1_tag || '',
      meta_author: data?.meta_author || '',
      is_active: data?.is_active !== undefined ? data.is_active : true,
      priority: data?.priority || 5
    };
  };
  
  const [formData, setFormData] = useState(() => initializeFormData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  // Helper to determine if form should be read-only
  const isReadOnly = mode === 'view';

  // Page choices from the model
  const pageChoices = [
    { value: 'home', label: 'Home Page' },
    { value: 'features', label: 'Features Page' },
    { value: 'ai-assistant', label: 'AI Assistant' },
    // { value: 'pricing', label: 'Pricing Page' },
    { value: 'blog', label: 'Blog Page' },
    { value: 'book-doctor', label: 'book-doctor Page' },
    { value: 'About us', label: 'About us Page' },
    { value: 'Privacy and security', label: 'Privacy and security Page' },
    // { value: 'contact', label: 'Contact Page' },
    // { value: 'about', label: 'About Us' },
    // { value: 'book-doctor', label: 'Book Doctor' },
    // { value: 'doctors', label: 'Doctors' },
    // { value: 'hospitals', label: 'Hospitals' },
    // { value: 'appointments', label: 'Appointments' },
    // { value: 'chat', label: 'Chat' },
    // { value: 'ai-chat', label: 'AI Chat' },
  ];

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000); // Shorter timeout for toast
  };

  // Fetch SEO content for edit/view mode
  const fetchSeoContent = async () => {
    console.log('🔍 fetchSeoContent called with:', { mode, seoId });
    
    if ((mode !== 'edit' && mode !== 'view') || !seoId) {
      console.log('❌ Not fetching - mode or seoId invalid');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      console.log('🔍 Token found:', token ? 'Yes' : 'No');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      // Ensure proper URL formatting
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const url = `${baseUrl}/api/seo/admin/${seoId}/`;
      
      console.log('🔍 Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ SEO data received:', data);
        setFormData(initializeFormData(data));
      } else {
        const errorText = await response.text();
        console.error('❌ Fetch error response:', errorText);
        
        let errorMessage = 'Failed to fetch SEO content';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('❌ Error fetching SEO content:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showNotification('Network error: Unable to connect to server. Please check your internet connection.', 'error');
      } else {
        showNotification(`Error fetching SEO content: ${error.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('Token found:', token ? 'Yes' : 'No');
      console.log('Token length:', token ? token.length : 0);
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        setSaving(false);
        return;
      }

      const method = mode === 'create' ? 'POST' : 'PUT';
      
      // Validate required fields
      if (!formData.page_key || !formData.page_name || !formData.title || !formData.description) {
        showNotification('Please fill in all required fields (Page Key, Page Name, Title, Description)', 'error');
        setSaving(false);
        return;
      }

      // Parse structured data
      let parsedStructuredData = {};
      try {
        parsedStructuredData = JSON.parse(formData.structured_data || '{}');
      } catch (error) {
        showNotification('Invalid JSON in structured data', 'error');
        setSaving(false);
        return;
      }

      const submitData = {
        ...formData,
        structured_data: parsedStructuredData
      };

      // Ensure proper URL formatting
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      
      const url = mode === 'create' 
        ? `${baseUrl}/api/seo/admin/create/`
        : `${baseUrl}/api/seo/admin/${seoId}/`;
      
      console.log('Making request to:', url);
      console.log('Request method:', method);
      console.log('Request data:', submitData);
      console.log('Authorization header:', `Bearer ${token.substring(0, 20)}...`);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        const responseData = await response.json();
        showNotification(
          mode === 'create' ? 'SEO content created successfully' : 'SEO content updated successfully'
        );
        
        // Navigate back after a short delay
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage = `Operation failed (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use the text as error message
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Network error:', error);
      
      // More specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showNotification('Network error: Unable to connect to server. Please check your internet connection and server status.', 'error');
      } else if (error.name === 'AbortError') {
        showNotification('Request timeout. Please try again.', 'error');
      } else {
        showNotification(`Network error: ${error.message}. Please check your connection.`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    // Initialize form data first to prevent controlled/uncontrolled input warnings
    if (Object.keys(formData).length === 0) {
      setFormData(initializeFormData());
    }
    
    // Then fetch SEO content if in edit or view mode
    if ((mode === 'edit' || mode === 'view') && seoId) {
      fetchSeoContent();
    }
  }, [mode, seoId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {mode === 'create' ? 'Create SEO Content' : mode === 'edit' ? 'Edit SEO Content' : 'View SEO Content'}
          </h1>
          <p className="text-slate-600">
            {mode === 'create' ? 'Configure SEO settings for your page' : mode === 'edit' ? 'Update SEO settings for your page' : 'View SEO settings for your page'}
          </p>
        </div>
      </div>

      {/* Toast Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.3 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.3 }}
          className={`fixed top-4 right-4 z-50 rounded-lg shadow-lg overflow-hidden min-w-[300px] max-w-[400px] ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="p-4 flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Progress bar */}
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: 3, ease: "linear" }}
            className={`h-1 ${
              notification.type === 'success' 
                ? 'bg-green-400' 
                : 'bg-red-400'
            }`}
          />
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic SEO Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-medical-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Basic SEO</h2>
              <p className="text-sm text-slate-600">Essential SEO information for your page</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Page Key *
                </label>
                <select
                  value={formData.page_key}
                  onChange={(e) => setFormData({ ...formData, page_key: e.target.value })}
                  disabled={mode === 'edit' || isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:bg-slate-100"
                  required
                >
                  <option value="">Select a page</option>
                  {pageChoices.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Page Name *
                </label>
                <input
                  type="text"
                  value={formData.page_name}
                  onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
                  readOnly={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent read-only:bg-slate-50"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Title * <span className="text-xs text-slate-500">({formData.title?.length || 0}/60 recommended)</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Description * <span className="text-xs text-slate-500">({formData.description?.length || 0}/160 recommended)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Keywords <span className="text-xs text-slate-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                placeholder="medical app, AI healthcare, doctor booking"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                H1 Tag
              </label>
              <input
                type="text"
                value={formData.h1_tag}
                onChange={(e) => setFormData({ ...formData, h1_tag: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
              />
            </div> */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Meta Author
                </label>
                <input
                  type="text"
                  value={formData.meta_author}
                  onChange={(e) => setFormData({ ...formData, meta_author: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Priority (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-medical-600 focus:ring-medical-500 border-slate-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-slate-700">
                Active
              </label>
            </div>
          </div>
        </motion.div>

        {/* Social Media Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Social Media</h2>
              <p className="text-sm text-slate-600">Open Graph and Twitter Card settings</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Open Graph */}
            <div>
              <h3 className="text-lg font-medium text-slate-800 mb-4">Open Graph (Facebook, LinkedIn)</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    OG Title
                  </label>
                  <input
                    type="text"
                    value={formData.og_title}
                    onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    OG Description
                  </label>
                  <textarea
                    value={formData.og_description}
                    onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      OG Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.og_image}
                      onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                    />
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      OG Type
                    </label>
                    <select
                      value={formData.og_type}
                      onChange={(e) => setFormData({ ...formData, og_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                    >
                      <option value="website">Website</option>
                      <option value="article">Article</option>
                      <option value="product">Product</option>
                    </select>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Twitter Cards */}
            <div>
              <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
                <Hash className="w-5 h-5" />
                Twitter Cards
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Twitter Title
                  </label>
                  <input
                    type="text"
                    value={formData.twitter_title}
                    onChange={(e) => setFormData({ ...formData, twitter_title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Twitter Description
                  </label>
                  <textarea
                    value={formData.twitter_description}
                    onChange={(e) => setFormData({ ...formData, twitter_description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Twitter Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.twitter_image}
                      onChange={(e) => setFormData({ ...formData, twitter_image: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Twitter Card Type
                    </label>
                    <select
                      value={formData.twitter_card_type}
                      onChange={(e) => setFormData({ ...formData, twitter_card_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                    >
                      <option value="summary">Summary</option>
                      <option value="summary_large_image">Summary Large Image</option>
                      <option value="app">App</option>
                      <option value="player">Player</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Technical Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Technical SEO</h2>
              <p className="text-sm text-slate-600">Advanced technical SEO settings</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Canonical URL
              </label>
              <input
                type="url"
                value={formData.canonical_url}
                onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Robots Meta
              </label>
              <select
                value={formData.robots_meta}
                onChange={(e) => setFormData({ ...formData, robots_meta: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
              >
                <option value="index, follow">Index, Follow</option>
                <option value="noindex, follow">No Index, Follow</option>
                <option value="index, nofollow">Index, No Follow</option>
                <option value="noindex, nofollow">No Index, No Follow</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Advanced Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Advanced</h2>
              <p className="text-sm text-slate-600">Structured data and advanced configurations</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Structured Data (JSON-LD)
              </label>
              <textarea
                value={formData.structured_data}
                onChange={(e) => setFormData({ ...formData, structured_data: e.target.value })}
                rows={10}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent font-mono text-sm"
                placeholder='{"@context": "https://schema.org", "@type": "WebPage"}'
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter valid JSON-LD structured data for rich snippets
              </p>
            </div>
          </div>
        </motion.div>

        {/* Submit Button - Hide in view mode */}
        {!isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-medical-500 text-white px-8 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {mode === 'create' ? 'Create SEO Content' : 'Update SEO Content'}
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Back Button for View Mode */}
        {isReadOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-end gap-3 pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="bg-slate-500 text-white px-8 py-2 rounded-lg hover:bg-slate-600 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to SEO Management
            </button>
          </motion.div>
        )}
      </form>
    </div>
  );
}